import React, { useCallback, ReactElement } from 'react';
import TableCell from '../../../../components/table-cell/table-cell.component';
import TableRow from '../../../../components/table-row/table-row.component';
import Tooltip from '../../../../components/tooltip/tooltip.component';
import Loader from '../../../../components/loader/loader.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import IconBlockError from '../../../../components/icon-block-error/icon-block-error.component';
import IconBlockSuccess from '../../../../components/icon-block-success/icon-block-success.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldUpload from '../../../../components/form-field-upload/form-field-upload.component';
import ActionButtonContainer from '../../../../components/action-button-container/action-button-container.component';
import RemoveIcon from '../../../../icons/remove-icon';
import FormService from '../../../../services/form-service';

const DealValuationFileRowForm: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isActive,
    isSaving,
    isSaveDisabled,
    isInlineSaving,
    isNewEntity,
    fileDropDeps,
    dealValuationFileData,
    dealValuationFileGroupData,
    dealValuationFileFile,
    onFileChange,
    onFileDelete,
    onDeleteClick,
    onInlineSave,
    onInlineCancel,
  } = props;

  const dealValuationFileId: number = (dealValuationFileData && dealValuationFileData.id) || FormService.FORM_KEY_NEW;
  const dealValuationFileGroupId: number = (dealValuationFileGroupData && dealValuationFileGroupData.id)
    || FormService.FORM_KEY_NEW;
  const formKeyDealValuationFile: string = FormService.getIndexedFormFieldKey(dealValuationFileId);
  const formKeyDealValuationFileGroup: string = FormService.getIndexedFormFieldKey(dealValuationFileGroupId);
  const formPrefix: string = `dealData.dealValuationFilesData[${formKeyDealValuationFileGroup}].`
    + `files[${formKeyDealValuationFile}]`;

  const onDealValuationFileFileChange: Function = useCallback((file: File): void => {
    if (onFileChange) {
      onFileChange(file, dealValuationFileGroupId, dealValuationFileId);
    }
  }, [dealValuationFileGroupId, dealValuationFileId, dealValuationFileFile]);

  const onDealValuationFileFileDelete: Function = useCallback((): void => {
    if (onFileDelete) {
      onFileDelete(dealValuationFileGroupId, dealValuationFileId);
    }
  }, [dealValuationFileGroupId, dealValuationFileId, dealValuationFileFile]);

  return (
    <TableRow isActive={isActive}>
      <TableCell>
        <FormFieldText isHidden name={`${formPrefix}.id`} />
        <FormFieldText name={`${formPrefix}.name`} placeholder="File Name" />
      </TableCell>
      <TableCell>
        {
          isNewEntity && (
            <FormFieldUpload
              isInline
              isDroppable
              withAllFilesAllowed
              uploadFile={dealValuationFileFile}
              name={`${formPrefix}.file`}
              buttonLabel="Upload New File"
              dropDeps={fileDropDeps}
              onFileChange={onDealValuationFileFileChange}
              onFileDelete={onDealValuationFileFileDelete}
            />
          )
        }
      </TableCell>
      <TableCell isTextRight width="5%">
        {
          !isNewEntity && isSaving && isInlineSaving && (
            <Loader isInline isSmall />
          )
        }
        <ActionButtonContainer>
          {
            !isNewEntity && !isSaveDisabled && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineSave(dealValuationFileGroupData.id, dealValuationFileData.id);
                }}
              >
                <IconBlockSuccess />
              </ActionButton>
            )
          }
          {
            !isNewEntity && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineCancel(dealValuationFileGroupData.id, dealValuationFileData.id);
                }}
              >
                <IconBlockError isDisabled={isSaveDisabled} />
              </ActionButton>
            )
          }
          {
            !isSaving && isNewEntity && (
              <Tooltip title="Delete">
                <ActionButton isInline withSmallPadding onClick={onDeleteClick}>
                  <RemoveIcon />
                </ActionButton>
              </Tooltip>
            )
          }
        </ActionButtonContainer>
      </TableCell>
    </TableRow>
  );
});

export default DealValuationFileRowForm;
